import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import type {
  WatchedItemActivityView,
  WatchedItemView,
  WatchlistView,
} from "../../shared/types/api";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Timeline,
  cn,
  type TimelineEntry,
} from "../../shared/ui";
import { WorkItemStatusBadge, WorkItemTypeBadge } from "../workitems/WorkItemTypeBadge";
import {
  formatTimestamp,
  historyValue,
  phraseHistoryEntry,
  showsValues,
} from "../workitems/workItemVocabulary";

/**
 * Work somebody is following, and what has happened to it.
 *
 * <p>Two panels rather than a list of events, because the question is "what happened to the
 * things I care about" and not "what happened". A single merged feed across thirty items reads
 * as noise: the eye has to reassemble which line belongs to which piece of work before any of it
 * means anything. Choosing the item first does that work once.
 *
 * <p>The selected item lives in the query string, so a particular item's activity is a link
 * somebody can send.
 */
export function WatchlistPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const chosen = params.get("item");
  const [error, setError] = useState<string | null>(null);

  /* The page itself always asks: somebody who has opened the watchlist is looking at it, which
     is the one moment the topbar's cached count is not good enough. */
  const watchlist = useQuery({
    queryKey: ["watchlist"],
    queryFn: () => api.get<WatchlistView>("/api/v1/watchlist"),
    refetchOnMount: "always",
  });

  const items = watchlist.data?.items ?? [];

  /* Opening the page with nothing chosen selects whatever moved most recently, which is what
     somebody came to look at. Left empty, the right-hand half of the screen says nothing. */
  useEffect(() => {
    const first = watchlist.data?.items[0];
    /* Only while the list is settled. Choosing from a list that is mid-refetch is choosing from
       yesterday's answer, which is how a dropped item reappeared. */
    if (!chosen && first && !watchlist.isFetching) {
      setParams({ item: first.workItemId }, { replace: true });
    }
  }, [chosen, watchlist.data, watchlist.isFetching, setParams]);

  const activity = useQuery({
    queryKey: ["watchlist", "activity", chosen],
    queryFn: () => api.get<WatchedItemActivityView>(`/api/v1/watchlist/${chosen}`),
    enabled: chosen != null,
  });

  /* Reading it is what marks it read, so the list's unread marks are stale the moment the
     right-hand panel loads. */
  useEffect(() => {
    if (activity.data) {
      void queryClient.invalidateQueries({ queryKey: ["watchlist"], exact: true });
    }
  }, [activity.data, queryClient]);

  const stopWatching = useMutation({
    mutationFn: (workItemId: string) => api.del(`/api/v1/watchlist/${workItemId}`),
    /*
     * Moves to the next item rather than clearing the selection. Clearing it let the effect
     * above choose again from a list that had not been refetched yet, so it picked the item
     * just dropped and put it straight back on screen — which read as the button not working.
     */
    onSuccess: (_result, stoppedId) => {
      const next = items.find((row) => row.workItemId !== stoppedId);
      setParams(next ? { item: next.workItemId } : {}, { replace: true });
      void queryClient.invalidateQueries({ queryKey: ["watchlist"] });
    },
    onError: () => setError("Could not stop watching that item."),
  });

  if (watchlist.isLoading) {
    return <LoadingState />;
  }
  if (watchlist.isError) {
    return <ErrorState error={watchlist.error} onRetry={() => void watchlist.refetch()} />;
  }

  return (
    <>
      <PageHeader
        title="Watching"
        subtitle="Work you have commented on, and everything that has happened to it since. Nothing here was sent to you — it is the record, kept."
      />

      {error && <div className="mb-4">{error}</div>}

      {items.length === 0 ? (
        <EmptyState
          title="You are not watching anything yet"
          message="Comment on a piece of work and it appears here, along with everything that happens to it afterwards. Stop watching whenever you like — commenting again brings it back."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
          <Card className="overflow-hidden">
            <ul className="divide-y divide-ink-100">
              {items.map((item) => (
                <li key={item.workItemId}>
                  <button
                    type="button"
                    onClick={() => setParams({ item: item.workItemId })}
                    className={cn(
                      "w-full px-4 py-3 text-left transition-colors hover:bg-ink-50",
                      item.workItemId === chosen && "bg-brand-50 hover:bg-brand-50",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <WorkItemTypeBadge type={item.workItemType} />
                      <span className="font-mono text-xs text-ink-500">{item.itemKey}</span>
                      {/* Said in words rather than as a bare number. "2" beside a task could be
                          anything — replies, hours, days — and the one thing somebody scanning
                          this list wants to know is how much of it they have not seen. */}
                      {item.unreadCount > 0 && (
                        <span className="ml-auto whitespace-nowrap rounded-full bg-brand-600 px-2 py-0.5 text-xs font-medium text-white">
                          {item.unreadCount} unread{" "}
                          {item.unreadCount === 1 ? "update" : "updates"}
                        </span>
                      )}
                    </div>
                    <p
                      className={cn(
                        "mt-1 text-sm text-ink-900",
                        item.unreadCount > 0 && "font-semibold",
                      )}
                    >
                      {item.title}
                    </p>
                    <p className="mt-1 truncate text-xs text-ink-500">
                      {item.lastActivitySummary
                        ? `${item.lastActivitySummary}${item.lastActorName ? ` · ${item.lastActorName}` : ""}`
                        : "Nothing has happened yet"}
                    </p>
                    <p className="text-xs text-ink-400">
                      {item.projectName}
                      {item.lastActivityAt ? ` · ${formatTimestamp(item.lastActivityAt)}` : ""}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            {activity.isLoading ? (
              <LoadingState />
            ) : activity.isError ? (
              <ErrorState error={activity.error} onRetry={() => void activity.refetch()} />
            ) : chosen && activity.data ? (
              /* Guarded on the selection as well as the data. A disabled query keeps whatever it
                 last fetched, so without this the panel went on showing an item seconds after
                 somebody pressed Stop watching on it — the one thing they had just said they did
                 not want to see. */
              <ItemActivity
                view={activity.data}
                onStopWatching={() => stopWatching.mutate(activity.data!.item.workItemId)}
                stopping={stopWatching.isPending}
              />
            ) : null}
          </Card>
        </div>
      )}
    </>
  );
}

/** One watched item's story, on the right. */
function ItemActivity({
  view,
  onStopWatching,
  stopping,
}: {
  view: WatchedItemActivityView;
  onStopWatching: () => void;
  stopping: boolean;
}) {
  const entries: TimelineEntry[] = view.activity.map((entry) => ({
    id: entry.id,
    title: (
      <span>
        <span className="font-medium text-ink-900">{entry.actorName ?? "Someone"}</span>{" "}
        {entry.transitionLabel
          ? `moved this — ${entry.transitionLabel}`
          : phraseHistoryEntry(entry.eventType, entry.fieldName)}
        {showsValues(entry.eventType) && entry.oldValue && entry.newValue ? (
          <>
            {" "}
            from{" "}
            <span className="text-ink-600">
              {historyValue(entry.eventType, entry.oldValue)}
            </span>{" "}
            to{" "}
            <span className="text-ink-900">
              {historyValue(entry.eventType, entry.newValue)}
            </span>
          </>
        ) : null}
      </span>
    ),
    detail: entry.reasonLabel ? <span>{entry.reasonLabel}</span> : undefined,
    meta: formatTimestamp(entry.eventTimestamp),
    emphasis: entry.transition != null,
  }));

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <WorkItemTypeBadge type={view.item.workItemType} />
            <Link
              to={`/work-items/${view.item.itemKey}`}
              className="font-mono text-xs text-brand-700 underline-offset-2 hover:underline"
            >
              {view.item.itemKey}
            </Link>
            <WorkItemStatusBadge status={view.item.status} />
          </div>
          <h2 className="mt-1 text-lg font-semibold text-ink-900">{view.item.title}</h2>
          <p className="text-sm text-ink-500">
            {view.item.projectName}
            {view.item.assigneeName ? ` · ${view.item.assigneeName}` : " · Unassigned"}
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          icon={<EyeOff className="h-4 w-4" />}
          onClick={onStopWatching}
          loading={stopping}
        >
          Stop watching
        </Button>
      </div>

      {view.activity.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-500">Nothing has happened yet.</p>
      ) : (
        <Timeline entries={entries} />
      )}
    </div>
  );
}

/** Kept for the status badge's tone lookup. */
export type { WatchedItemView };
